import { useState, useEffect, useCallback } from 'react';
import { rainfallApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CloudRain, Plus, Droplets, Calendar, TrendingUp, Cloud, Trash2, RefreshCw } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function RainfallPage() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Form
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formAmount, setFormAmount] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([rainfallApi.getAll(), rainfallApi.getSummary()]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error('Failed to load rainfall data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formAmount || parseFloat(formAmount) < 0) { toast.error('Enter a valid rainfall amount'); return; }
    try {
      await rainfallApi.create({ date: formDate, amount_mm: parseFloat(formAmount), source: 'manual', notes: formNotes || null });
      toast.success('Rainfall recorded');
      setDialogOpen(false);
      setFormAmount(''); setFormNotes('');
      loadData();
    } catch { toast.error('Failed to save'); }
  };

  const handleFetchWeather = async () => {
    setFetching(true);
    try {
      const res = await rainfallApi.fetchWeather();
      const data = res.data;
      if (data.rain_mm > 0) {
        setFormAmount(String(data.rain_mm));
        setFormNotes(`Auto: ${data.conditions || ''} (${data.temp || 0}°C)`);
        toast.success(`Current rain: ${data.rain_mm}mm`);
      } else {
        toast.info(data.message || 'No rain detected currently');
        setFormAmount('0');
        setFormNotes(`Auto: ${data.conditions || 'No rain'} (${data.temp || 0}°C)`);
      }
    } catch (err) {
      console.error('[TF] Weather fetch error:', err?.response?.status, err?.response?.data, err?.message);
      toast.error('Could not fetch weather - ' + (err?.response?.data?.detail || err?.message || 'try again'));
    }
    finally { setFetching(false); }
  };

  const handleDelete = async (id) => {
    try {
      await rainfallApi.delete(id);
      toast.success('Entry deleted');
      loadData();
    } catch { toast.error('Delete failed'); }
  };

  const maxMm = Math.max(...(summary?.monthly?.map(m => m.total_mm) || [1]), 1);

  if (loading) return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="rainfall-loading" />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rainfall-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Rainfall</h1>
          <p className="text-muted-foreground">Track daily rainfall totals for your farm</p>
        </div>
        <Button className="btn-primary" onClick={() => { setFormDate(new Date().toISOString().split('T')[0]); setFormAmount(''); setFormNotes(''); setDialogOpen(true); }} data-testid="add-rainfall-btn">
          <Plus className="h-4 w-4 mr-2" /> Record Rainfall
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card" data-testid="rainfall-7d">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Droplets className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last 7 Days</p>
                <p className="text-2xl font-heading font-bold text-blue-700">{summary?.last_7_days || 0} mm</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="rainfall-30d">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <CloudRain className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last 30 Days</p>
                <p className="text-2xl font-heading font-bold text-blue-700">{summary?.last_30_days || 0} mm</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="rainfall-ytd">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Year to Date</p>
                <p className="text-2xl font-heading font-bold text-blue-700">{summary?.year_to_date || 0} mm</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart */}
      {summary?.monthly?.length > 0 && (
        <Card data-testid="rainfall-chart">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-blue-600" /> Monthly Rainfall</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-48">
              {summary.monthly.map((m) => {
                const pct = maxMm > 0 ? (m.total_mm / maxMm) * 100 : 0;
                const label = m.month.split('-');
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] font-mono text-muted-foreground">{m.total_mm}</span>
                    <div className="w-full bg-blue-100 rounded-t relative" style={{ height: `${Math.max(pct, 4)}%` }}>
                      <div className="absolute inset-0 bg-blue-500 rounded-t opacity-80" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">{MONTHS[parseInt(label[1], 10) - 1] || label[1]}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries Table */}
      <Card data-testid="rainfall-entries">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-600" /> Rainfall Log ({entries.length} entries)</CardTitle></CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No rainfall recorded yet. Click "Record Rainfall" to add your first entry.</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {entries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg border hover:bg-secondary/30 transition-colors" data-testid={`rainfall-entry-${entry.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <CloudRain className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{entry.date}</p>
                      {entry.notes && <p className="text-xs text-muted-foreground">{entry.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={entry.source === 'auto' ? 'secondary' : 'outline'} className="text-xs">
                      {entry.source === 'auto' ? <Cloud className="h-3 w-3 mr-1" /> : null}
                      {entry.source}
                    </Badge>
                    <span className="font-heading font-bold text-lg text-blue-700">{entry.amount_mm} mm</span>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(entry.id)} data-testid={`delete-rainfall-${entry.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-rainfall-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><CloudRain className="h-5 w-5 text-blue-600" /> Record Rainfall</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} required data-testid="rainfall-date-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Amount (mm)</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleFetchWeather} disabled={fetching} className="text-xs h-7" data-testid="fetch-weather-btn">
                  <RefreshCw className={`h-3 w-3 mr-1 ${fetching ? 'animate-spin' : ''}`} /> {fetching ? 'Fetching...' : 'Auto-fetch from weather'}
                </Button>
              </div>
              <Input type="number" step="0.1" min="0" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} placeholder="e.g. 12.5" required data-testid="rainfall-amount-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Storm, light drizzle, etc." data-testid="rainfall-notes-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="rainfall-submit-btn">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
